/**
 * The marketing site's public routes — the single source of truth.
 *
 * This list used to be duplicated (with slightly different contents) in
 * AuthGuard, ConditionalLayout, sitemap.ts and robots.ts. The copies
 * drifted as pages were added, and each omission failed differently and
 * silently:
 *
 *   - missing in AuthGuard         → the route server-renders as a
 *                                    full-screen <Loader />, so crawlers
 *                                    index an empty page
 *   - missing in ConditionalLayout → the marketing page renders wrapped
 *                                    in the authenticated admin sidebar
 *                                    and header, which also drags
 *                                    useSearchParams() into a statically
 *                                    prerendered route and fails the
 *                                    production build
 *   - missing in sitemap.ts        → the page is never submitted to
 *                                    Google
 *
 * Add new marketing pages here and nowhere else.
 */
export const MARKETING_PUBLIC_PATHS = [
  '/',
  '/features',
  '/pricing',
  '/plans',
  '/contact',
  '/blog',
  '/support',
  // The five routes below are ARCHIVED: their pages 404 on the marketing
  // host (dummy/placeholder data) until real content relaunches them. They
  // stay in this list on purpose — removing one makes AuthGuard treat the
  // URL as a portal route, so a visitor typing it gets bounced through the
  // auth flow instead of the clean 404 the page itself serves.
  '/case-studies',
  '/help-center',
  '/user-guides',
  '/video-tutorials',
  '/system-status',
  '/privacy-policy',
  '/terms-and-conditions',
  '/refund-policy',
];

/**
 * True when `pathname` is a marketing page. Matches the path exactly, or
 * as a parent segment (so `/blog/some-post` counts as public). `/` is
 * matched exactly only — every path starts with it.
 */
export const isMarketingPublicPath = (pathname) => {
  if (!pathname) return false;
  if (pathname === '/' || pathname === '') return true;
  return MARKETING_PUBLIC_PATHS.some(
    (base) => base !== '/' && (pathname === base || pathname.startsWith(`${base}/`)),
  );
};
